import { Base } from './LanguageTools.js';

// Compile a dot-scoped listener pattern. '*' matches a single segment (no dots) and
// captures it; '**' matches any run of characters (dots included) and captures it;
// everything else is literal. Returns { re, globs } where globs records the kind of
// each capture in order, so a match can report what each glob caught.
const glob_matcher = (pattern) => {
    const globs = [];
    const source = '^' + pattern.replace(/\*\*|\*|[.+?^${}()|[\]\\]/g, (tok) => {
        if ( tok === '**' ) { globs.push('**'); return '(.*)'; }   // any run, across dots
        if ( tok === '*' ) { globs.push('*'); return '([^.]+)'; } // one segment
        return '\\' + tok;                                         // escape a regex special (incl. '.')
    }) + '$';
    return { re: new RegExp(source), globs };
};

// Match an event name against a compiled pattern. Returns null if it doesn't match,
// else the `matches` array: one entry per glob, a string for '*' and a nested array of
// segments for '**' (its whole captured run, split back on the dots).
const match_event = ({ re, globs }, name) => {
    const m = re.exec(name);
    if ( ! m ) return null;
    return globs.map((kind, i) => {
        const caught = m[i + 1];
        if ( kind !== '**' ) return caught;                 // '*' -> the segment
        return caught === '' ? [] : caught.split('.');      // '**' -> nested array
    });
};

// Every function on an instance's prototype chain (skipping constructors and
// Object.prototype), so `on_<name>` handlers can be discovered.
const prototype_methods = (obj) => {
    const names = new Set();
    let proto = Object.getPrototypeOf(obj);
    while ( proto && proto !== Object.prototype ) {
        for ( const name of Object.getOwnPropertyNames(proto) ) {
            if ( name !== 'constructor' && typeof proto[name] === 'function' ) names.add(name);
        }
        proto = Object.getPrototypeOf(proto);
    }
    return names;
};

// The Kernel: one instance owns a set of NAMED services and boots them together. The
// services are built first, then handed to the Kernel keyed by name; the Kernel gives
// each of them a `serviceAPI` — the handle they use to reach their siblings — and drives
// them through the boot lifecycle.
//
// Coordination between services (per SOA-PLAN):
//   - to ask a sibling for something, look it up and call it: `this.serviceAPI.get('worlds')`
//   - events (via EmitterFeature) are for notifications; a listener that needs to
//     influence an outcome is handed an `api` object by whoever emits the event.
//
// The same Kernel runs a server (services: web, net, worlds, clock, …) and a browser
// client (services: net, render, input, ui, audio) — the glue is isomorphic.
export class Kernel {
    constructor ({ services = {} } = {}) {
        this.services = services; // name -> Service instance

        // The API every service uses to reach the others. It's shared: any service can
        // resolve any sibling by name. (Named `serviceAPI` and set on each service so a
        // handler can write `this.serviceAPI.get(name)`.)
        //
        // `get` returns undefined for a missing sibling; `require` throws. Reach for
        // `require` whenever the sibling is mandatory (which is almost always, in
        // `consolidate`) — a wiring typo or a service dropped from the Kernel then fails
        // loudly at boot, naming what's missing, instead of surfacing as a bewildering
        // `undefined` the first time something calls a method on it mid-game.
        this.serviceAPI = {
            get: (name) => this.services[name],
            has: (name) => Object.prototype.hasOwnProperty.call(this.services, name),
            names: () => Object.keys(this.services),
            require: (name) => {
                const service = this.services[name];
                if ( ! service ) throw new Error(`serviceAPI.require: no service "${name}" (have: ${Object.keys(this.services).join(', ') || 'none'})`);
                return service;
            },
        };
        for ( const [name, service] of Object.entries(this.services) ) {
            service.serviceName = name;
            service.serviceAPI = this.serviceAPI;
        }
    }

    // Boot the services through the lifecycle, one phase across ALL services before the
    // next — so every service can reach the others' finished work when it needs to:
    //   init         create your own resources (open nothing outward yet)
    //   consolidate  look up siblings and register event listeners
    //   ready        start doing work (open the socket, start the clock / render loop)
    // A service handles a phase with an `on_phase_init()` method (or `this.on(...)`). Any
    // phase may be async; the Kernel waits for every service to settle before the next.
    async boot () {
        const phases = ['init', 'consolidate', 'ready'];
        for ( const phase of phases ) {
            await Promise.all(
                Object.values(this.services)
                    .filter((service) => typeof service.emit === 'function')
                    .map((service) => service.emit(`phase.${phase}`))
            );
        }
    }
}

export class EmitterFeature {
    static SPEC = `
        A conventional event emitter feature.

        The 'emit' method should invoke all listeners in sequence and if
        any listener returns a promise it will add the promise to a list
        of promises to await at the very end. At the end emit waits for
        all of those promises using Promise.all.

        When a class has EmitterFeature installed, it can listen to events
        with \`this.on(...)\` or by implementing an \`on_<name>()\` method.
        The \`on_<name>()\` method can use subsequent underscores instead of
        dots, or also dots may be used with \`['this.syntax'] () {}\`.

        Event names can be scoped with a dot delimiter, and globs can be
        used when registering a listener.

        An event listener should be passed the following arguments:
        - name: the name of the event that fired
        - matches: array of things globbed (\`**\` creates a nested array)
        - ...args: arguments passed to emit
    `

    static install_in_instance (instance) {
        const listeners = []; // { matcher, fn }
        const add = (pattern, fn) => listeners.push({ matcher: glob_matcher(pattern), fn });

        // Register a listener. `pattern` is a dot-scoped event name that may use '*'
        // and '**' globs. Returns the instance so calls can be chained.
        instance.on = (pattern, fn) => { add(pattern, fn); return instance; };

        // Fire an event: run every matching listener in registration order. Each is
        // passed the event name and the `matches` its pattern globbed, followed by any
        // extra args given to emit. Promises listeners return are collected and awaited
        // (all together) before emit resolves.
        instance.emit = async (name, ...args) => {
            const pending = [];
            for ( const { matcher, fn } of listeners ) {
                const matches = match_event(matcher, name);
                if ( matches === null ) continue;
                const result = fn.call(instance, name, matches, ...args);
                if ( result && typeof result.then === 'function' ) pending.push(result);
            }
            if ( pending.length ) await Promise.all(pending);
        };

        // An `on_<a>_<b>()` method listens for the event "a.b". Underscores are the
        // dot-scope separator; a name that needs literal dots can be written with a
        // computed key, `['on_a.b']() {}` — either way the dots are the scope.
        for ( const method of prototype_methods(instance) ) {
            if ( ! method.startsWith('on_') ) continue;
            const event = method.slice(3).split('_').join('.');
            add(event, (...a) => instance[method](...a));
        }
    }
}

export class Service extends Base {
    static FEATURES = [
        EmitterFeature,
    ];
}
